import { formatters, useTranslation } from "@equiem/web-ng-lib";
import { PageTitle, Site, stringNotEmpty, Theme, Viewer } from "@equiem/web-ng-lib";
import { useRouter } from "next/router";
import React, { MutableRefObject, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import { BsDownload, BsLink45Deg } from "react-icons/bs";
import scrollIntoView from "scroll-into-view";
import { PageViewPageType } from "../generated/gateway-client";
import { useLinktype } from "../hooks/useLinkType";
import { arrayNotEmpty } from "../lib/arrayNotEmpty";
import { ContentType } from "../lib/ContentType";
import { DateTime } from "../lib/DateTime";
import { CommentsForContent } from "./comments/CommentsForContent";
import { CortexContentEndMetric } from "./cortex/CortexContentEndMetric";
import { CortexEntityViewMetric } from "./cortex/CortexEntityViewMetric";
import { CortexPageViewMetric } from "./cortex/CortexPageViewMetric";
import { ImageSwiper } from "./ImageSwiper";
import { Pill } from "./Pill";
import { TrendingContent } from "./TrendingContent";

interface Props {
  uuid: string;
  contentType: ContentType;
  title: string;
  images: string[];
  author?: {
    name: string;
    avatar?: string | null;
  };
  headerRight?: JSX.Element;
  publishDate?: DateTime;
  actions?: React.ReactNode;
  commentsEnabled: boolean;
  enableEntityViewMetric?: boolean;
  pageMetricName?: "page_view" | "anonymous_page_view";
  categories?: string[];
  commentRef?: MutableRefObject<HTMLTextAreaElement | undefined>;
  downloads?: Array<{
    url: string;
    description: string;
  }>;
  relatedInformation?: Array<{
    title: string;
    url: string;
  }>;
}

export const PostContentPage: React.FC<PropsWithChildren<Props>> = ({
  uuid,
  contentType,
  author,
  publishDate,
  title,
  images,
  headerRight,
  children,
  actions,
  commentsEnabled,
  enableEntityViewMetric = true,
  pageMetricName,
  categories,
  commentRef: inputCommentRef,
  relatedInformation,
  downloads,
}) => {
  const { t, i18n } = useTranslation();
  const { timezone } = useContext(Site);

  const ref = useRef<HTMLDivElement>(null);
  const viewer = useContext(Viewer);
  const commentRef = inputCommentRef ?? useRef<HTMLTextAreaElement | undefined>(undefined);
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);
  const [top, setTop] = useState(0);
  const theme = useContext(Theme);
  const router = useRouter();
  const actionsWidth = 64;
  const padding = 30;
  const extraInfoExist = arrayNotEmpty(relatedInformation) || arrayNotEmpty(downloads);
  let pageType: PageViewPageType;
  const { isExternalLink } = useLinktype();

  const selectComment = typeof router.query.comment === "string" ? router.query.comment : undefined;

  switch (contentType) {
    case ContentType.BuildingInfoPost:
      pageType = PageViewPageType.Info;
      break;

    case ContentType.EventPost:
      pageType = PageViewPageType.Event;
      break;

    case ContentType.NewsPost:
      pageType = PageViewPageType.News;
      break;
  }

  useEffect(() => {
    setTop(ref.current?.getBoundingClientRect()?.top ?? 120 + document.documentElement.scrollTop);
    setPageLoaded(true);
  }, []);

  useEffect(() => {
    if (selectComment != null && commentRef.current != null) {
      scrollIntoView(commentRef.current);
    }
  }, [selectComment]);

  return (
    <div className="content-page d-flex flex-column">
      <PageTitle>{title}</PageTitle>
      <Container fluid="lg" as="article" className="py-4 px-3">
        <header>
          <h1
            className="pt-2 font-weight-bold"
            style={{ fontSize: "1.75rem" }}
          >
            {title}
          </h1>
          <div className="d-flex flex-wrap pt-2 pb-4">
            <div>
              {author?.avatar != null ? (
                <img
                  src={author.avatar}
                  className="rounded-circle mr-2 mr-lg-3"
                  style={{ width: "40px" }}
                />
              ) : null}
              <small className="text-muted">
                {author?.name != null ? (
                  <span className="d-inline mr-1">{author.name},</span>
                ) : null}
                {
                  publishDate != null ? (
                    <time
                      title={`${formatters.dateshort(publishDate.valueOf(), i18n.language, { timezone })} ${formatters.timeshort(publishDate.valueOf(), i18n.language, { timezone })}`}
                      dateTime={publishDate.toISO()}
                    >
                      {formatters.dateshort(publishDate.valueOf(), i18n.language, { timezone })}
                    </time>
                  ) : null
                }
              </small>
            </div>
            {headerRight != null ? (
              <div className="ml-auto d-flex align-items-center">
                {headerRight}
              </div>
            ) : null}
          </div>
          {categories != null && categories.length > 0 ? (
            <div className="pb-4">
              {categories.map((category) => (
                <Pill key={category} className="mr-1 mb-1">
                  {category}
                </Pill>
              ))}
            </div>
          ) : null}
          <ImageSwiper images={images} />
        </header>
        <section>{children}</section>
        {extraInfoExist ? (
          <Row>
            {arrayNotEmpty(relatedInformation) ? (
              <Col md="6" className="related-info-section d-flex pt-3 pb-4">
                <div className="text-muted">
                  <BsLink45Deg size="22" />
                </div>
                <div className="pl-1">
                  <div className="pb-2 font-weight-bold">{t("main.relatedInfo")}</div>
                  {relatedInformation.map((info, i) => (
                    <div className="pb-2" key={i}>
                      <a
                        href={info.url}
                        target={isExternalLink(info.url) ? "_blank" : undefined}
                        rel="noopener noreferrer"
                      >
                        {info.title}
                      </a>
                    </div>
                  ))}
                </div>
              </Col>
            ) : null}
            {arrayNotEmpty(downloads) ? (
              <Col md="6" className="downloads-section d-flex pt-3 pb-4">
                <div className="text-muted">
                  <BsDownload size="18" className="mt-n1" />
                </div>
                <div className="pl-2">
                  <div className="pb-2 font-weight-bold">{t("main.downloads")}</div>
                  {downloads.map((d, i) => (
                    <div className="pb-2" key={i}>
                      <a href={d.url} target="_blank" rel="noopener noreferrer">
                        {stringNotEmpty(d.description) ? d.description : t("main.file")}
                      </a>
                    </div>
                  ))}
                </div>
              </Col>
            ) : null}
          </Row>
        ) : null}
        {actions != null ? (
          <aside className="actions-container" ref={ref}>
            <div className="actions">{actions}</div>
          </aside>
        ) : null}
        {pageLoaded ? (
          <CortexContentEndMetric
            contentUuid={uuid}
            contentType={pageType}
            contentTitle={title}
            categories={categories}
          />
        ) : null}
      </Container>
      {enableEntityViewMetric ? (
        <CortexEntityViewMetric entityType={contentType} uuid={uuid} />
      ) : null}
      <CortexPageViewMetric
        pageViewType={pageType}
        pageTitle={title}
        categories={categories}
        metric={pageMetricName}
        crossReference={uuid}
      />
      {commentsEnabled ? (
        <div className="comments-bg p-3" style={{ flexGrow: 1 }}>
          {viewer?.permissions.readComments === true ? (
              <div className="pb-3">
                <h3 className="h5 text-center">{t("main.comments")}</h3>
                <Container fluid="lg" className="comments p-0 pt-3">
                  <CommentsForContent
                    contentUuid={uuid}
                    contentType={contentType}
                    inputRef={commentRef}
                    selectComment={selectComment}
                  />
                </Container>
              </div>
          ) : null}
          <TrendingContent excludeUuid={uuid} />
        </div>
      ) : null}
      <style jsx>{`
        .content-page :global(article) {
          position: relative;
        }
        .actions-container {
          position: sticky;
          bottom: 0;
          z-index: 1;
        }
        .actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding: 1rem;
        }
        .comments-bg {
          background-color: #f9f9f9;
        }
        @media screen and (min-width: ${theme.breakpoints.md}px) {
          .content-page :global(article),
          .content-page :global(.comments) {
            max-width: calc(100% - ${actionsWidth * 2}px);
            margin: 0 auto;
          }
          .actions-container {
            position: absolute;
            bottom: auto;
            top: 0;
            left: 0;
            height: 100%;
            width: ${actionsWidth}px;
            margin-left: -${actionsWidth}px;
          }
          .actions {
            flex-direction: column;
            position: sticky;
            top: ${top}px;
            padding-top: 2rem;
          }
        }
        @media screen and (min-width: ${theme.breakpoints.lg}px) {
          .content-page :global(article),
          .content-page :global(.comments) {
            max-width: ${theme.breakpoints.md - actionsWidth + padding}px;
            margin: 0 auto;
          }
        }
        @media screen and (min-width: ${theme.breakpoints.xl}px) {
          .actions-container {
            margin-left: -6rem;
          }
        }
      `}</style>
    </div>
  );
};
